// Leave delegation handover — pure rules shared by the leave form, the
// delegate inbox and the leave history. No I/O, no React: every rule here is
// unit-tested (handover.test.ts) and mirrored on mobile
// (kazipeople-mobile/src/features/delegation/handover.ts).
//
// Tolerant by design: the backend is adding leave-status info and a
// coverage-only mode (capabilities may be [] when origin=leave). Every field
// read here is optional, and an absent field never reads as good news.
import type { Delegation } from '@/services/rbacService';

/** Server limit for delegate_note (auth-service PATCH /rbac/delegations/:id). */
export const HANDOVER_NOTE_MAX = 2000;

/** Statuses after which a delegation no longer does anything. */
const ENDED = new Set(['revoked', 'rejected', 'declined', 'expired']);

/** Fields the backend may add for the delegate's view of the linked leave.
 *  Read defensively — camelCase or snake_case, boolean or status string. */
export interface LeaveInfoFields {
  leaveApproved?: boolean | null;
  leave_approved?: boolean | null;
  leaveStatus?: string | null;
  leave_status?: string | null;
  leaveTypeName?: string | null;
  leave_type_name?: string | null;
  leaveType?: string | null;
  leave?: { status?: string | null; approved?: boolean | null; typeName?: string | null; type?: string | null } | null;
}
export type HandoverDelegation = Delegation & LeaveInfoFields;

export function firstName(name?: string | null): string {
  const n = (name ?? '').trim();
  return n ? n.split(/\s+/)[0] : '';
}

function toMs(iso?: string | null): number | null {
  if (!iso) return null;
  // A bare civil date (YYYY-MM-DD) is the START of that local day.
  const t = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T00:00:00`).getTime() : new Date(iso).getTime();
  return Number.isFinite(t) ? t : null;
}

export type HandoverLockReason = 'ended' | 'started' | null;

/** Is the cover actually IN FORCE — i.e. has the handover happened?
 *  Not while the delegation still awaits the delegate's acceptance or an
 *  admin's approval, and — for a delegation that exists for a leave — not
 *  until that leave is APPROVED (an unsubmitted or pending request has handed
 *  nothing over yet). Mirrors auth-service coverHasStarted(). */
export function coverInForce(d: Partial<HandoverDelegation> | null | undefined): boolean {
  if (!d) return false;
  const s = String(d.status ?? '');
  if (s.startsWith('pending') || s === 'draft') return false;
  if (d.leaveRequestId || d.origin === 'leave') return linkedLeaveStatus(d as HandoverDelegation) === 'approved';
  return true;
}

/** Can the delegator still change the handover message?
 *  Only an ENDED delegation (revoked / declined / rejected / expired) freezes
 *  it. A note is guidance, not authority: someone who goes off sick at 8 must
 *  be able to write the instructions at 9, after cover has begun (owner
 *  decision 2026-10-22). Every change is audited server-side with the previous
 *  text. Changing WHO covers or the DATES is a separate, stricter operation —
 *  see structureLock. Mirrors auth-service update(). */
export function handoverLock(
  d: (Pick<Delegation, 'startsAt' | 'status' | 'revokedAt'> & Partial<HandoverDelegation>) | null | undefined,
  _leaveStart?: string | null,
  _now: number = Date.now(),
): HandoverLockReason {
  if (!d) return null;
  if (d.revokedAt || ENDED.has(String(d.status ?? ''))) return 'ended';
  return null;
}

/** Can the delegate, dates or scope still be changed? Not on an ended
 *  delegation, and not once the cover is IN FORCE (coverInForce) and its
 *  window — or the leave — has started: the delegate is already standing in.
 *  End it and set up another instead. Mirrors auth-service coverHasStarted(). */
export function structureLock(
  d: (Pick<Delegation, 'startsAt' | 'status' | 'revokedAt'> & Partial<HandoverDelegation>) | null | undefined,
  leaveStart?: string | null,
  now: number = Date.now(),
): HandoverLockReason {
  if (!d) return null;
  if (d.revokedAt || ENDED.has(String(d.status ?? ''))) return 'ended';
  if (!coverInForce(d)) return null;
  const starts = [toMs(d.startsAt), toMs(leaveStart ?? null)].filter((x): x is number => x != null);
  if (starts.some((s) => s <= now)) return 'started';
  return null;
}

/** Leave status as the delegate should see it: 'pending' | 'approved' | other
 *  status string | null when the backend did not say. */
export function linkedLeaveStatus(d: HandoverDelegation): string | null {
  if (!d.leaveRequestId) return null;
  const approved = d.leaveApproved ?? d.leave_approved ?? d.leave?.approved;
  if (approved === true) return 'approved';
  const s = (d.leaveStatus ?? d.leave_status ?? d.leave?.status ?? '').toString().trim().toLowerCase();
  if (s) return s;
  if (approved === false) return 'pending';
  return null;
}

export function leaveTypeLabel(d: HandoverDelegation): string {
  return (d.leaveTypeName ?? d.leave_type_name ?? d.leave?.typeName ?? d.leaveType ?? d.leave?.type ?? '').toString().trim() || 'Time off';
}

/** Coverage-only: the delegate covers the person's work but receives no
 *  approval rights (capabilities may be [] when origin=leave). */
export function isCoverageOnly(d: Pick<Delegation, 'capabilities'>): boolean {
  return !Array.isArray(d.capabilities) || d.capabilities.length === 0;
}

export interface DelegateInboxGroups {
  awaiting: HandoverDelegation[];
  upcoming: HandoverDelegation[];
  active: HandoverDelegation[];
  past: HandoverDelegation[];
}

/** Group "delegated to me" rows for the delegate's inbox. */
export function groupDelegateInbox(rows: HandoverDelegation[], now: number = Date.now()): DelegateInboxGroups {
  const g: DelegateInboxGroups = { awaiting: [], upcoming: [], active: [], past: [] };
  for (const d of rows ?? []) {
    const s = String(d.status ?? '');
    const ends = toMs(d.endsAt);
    const starts = toMs(d.startsAt);
    const leave = linkedLeaveStatus(d);
    if (s === 'pending_acceptance') g.awaiting.push(d);
    else if (d.revokedAt || ENDED.has(s) || (ends != null && ends <= now) || ['cancelled', 'rejected'].includes(leave ?? '')) g.past.push(d);
    else if ((s === 'active' || (!s && d.active)) && (starts == null || starts <= now)) g.active.push(d);
    else g.upcoming.push(d);
  }
  const byStart = (a: Delegation, b: Delegation) => String(a.startsAt).localeCompare(String(b.startsAt));
  g.awaiting.sort(byStart);
  g.upcoming.sort(byStart);
  g.active.sort(byStart);
  g.past.sort((a, b) => -byStart(a, b));
  return g;
}

/** Human status for a delegation row. */
export function delegationStatusLabel(d: Pick<Delegation, 'status' | 'revokedAt' | 'active'>): string {
  const s = d.status || (d.revokedAt ? 'revoked' : d.active ? 'active' : 'scheduled');
  const labels: Record<string, string> = {
    pending_acceptance: 'Awaiting acceptance',
    pending_approval: 'Pending approval',
    scheduled: 'Scheduled',
    active: 'Active',
    expired: 'Completed',
    revoked: 'Cancelled',
    rejected: 'Rejected',
    declined: 'Declined',
    draft: 'Draft',
  };
  return labels[s] ?? s.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

/** Deep link into a specific delegation (web): `.../app/admin/delegations?id=…`. */
export const DELEGATION_ID_PARAM = 'id';

/** Open the delegations page to create the REPLACEMENT cover for one leave
 *  request: `?replaceFor=<leaveRequestId>&from=YYYY-MM-DD&to=YYYY-MM-DD`. The
 *  new delegation is created linked to that leave and attached to it, so it
 *  shows "Related absence" (QA L10, 2026-10-24). A full page load drops router
 *  state, which is why this travels in the URL. */
export const REPLACE_FOR_PARAM = 'replaceFor';

export function replacementQuery(leaveRequestId: string, startDate?: string, endDate?: string): string {
  const q = new URLSearchParams({ [REPLACE_FOR_PARAM]: leaveRequestId });
  if (startDate) q.set('from', startDate);
  if (endDate) q.set('to', endDate);
  return q.toString();
}

/** Today's civil date (YYYY-MM-DD) in the device's zone. */
export function localCivilToday(now: number = Date.now()): string {
  const d = new Date(now);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

const CONFIRMED_COVERAGE = new Set(['active', 'scheduled']);
const DEAD_COVERAGE = new Set(['declined', 'revoked', 'rejected', 'expired']);

/** What the leave form tells the requester about coverage, and whether Submit
 *  is blocked. Mirrors leave-service delegation_link.coverage_gate('submit').
 *
 *  ⚠️ Owner decision 2026-10-22 (Amos): reporting an absence must NEVER depend
 *  on another person accepting a delegation. A delegate who has not accepted
 *  yet never blocks Submit — for any leave type, past or future (this
 *  replaces J8's "only leave that already started" exception). The only
 *  blocks: the policy requires a delegate and none is NAMED (or attested), or
 *  the one named has declined / been cancelled. Acceptance is shown as the
 *  coverage status the approver sees, not a gate. */
export type CoverageSubmitState = 'not_required' | 'missing' | 'dead' | 'confirmed' | 'pending';

export function coverageSubmitState(input: {
  required?: boolean | null;
  confirmation?: string | null;
  status?: string | null;
  hasCoverage: boolean;
  startDate?: string | null;
  today?: string;
}): { state: CoverageSubmitState; blocked: boolean } {
  const s = String(input.status ?? '');
  const started = !!input.startDate && input.startDate.slice(0, 10) <= (input.today ?? localCivilToday());
  if (!input.hasCoverage) return input.required ? { state: 'missing', blocked: true } : { state: 'not_required', blocked: false };
  if (DEAD_COVERAGE.has(s) && !(s === 'expired' && started)) return { state: 'dead', blocked: true };
  if (CONFIRMED_COVERAGE.has(s) || s === 'expired') return { state: 'confirmed', blocked: false };
  return { state: 'pending', blocked: false };
}

// ── Coverage status (derived; leave-service delegation_link.coverage_status) ─
// Three statuses never conflated: LEAVE (pending/approved/…), DELEGATION
// (pending_acceptance/active/declined/…) and COVERAGE — derived from the two
// plus the policy, shown to the employee and the approver.
export type CoverageStatus = 'not_required' | 'unassigned' | 'pending' | 'covered' | 'needs_attention';

/** Label + tone for a coverage status the server sent (unknown → null: an
 *  absent status is never shown as "covered"). */
export function coverageStatusMeta(status?: string | null): { label: string; tone: 'success' | 'warning' | 'danger' | 'muted' } | null {
  switch (status) {
    case 'covered': return { label: 'Covered', tone: 'success' };
    case 'pending': return { label: 'Cover pending acceptance', tone: 'warning' };
    case 'unassigned': return { label: 'No cover assigned yet', tone: 'warning' };
    case 'needs_attention': return { label: 'Cover needs attention', tone: 'danger' };
    case 'not_required': return { label: 'No cover needed', tone: 'muted' };
    default: return null;
  }
}

/** Why coverage needs attention, in words (reason codes from leave-service). */
export function coverageReasonText(reason?: string | null, delegateName?: string | null): string {
  const who = firstName(delegateName) || 'The delegate';
  switch (reason) {
    case 'none_named': return 'No delegate has been named, although company policy requires one.';
    case 'declined': return `${who} declined to cover.`;
    case 'revoked':
    case 'rejected': return 'The delegation was cancelled.';
    case 'expired': return 'The delegation ended before the time off.';
    case 'unaccepted_near_start': return `${who} hasn't accepted yet and the time off starts soon.`;
    case 'manager_to_assign_soon': return 'Nobody has been assigned to cover and the time off starts soon.';
    case 'manager_to_assign': return 'The manager arranges cover for this type of leave.';
    case 'awaiting_acceptance': return `${who} hasn't accepted yet.`;
    default: return '';
  }
}

// ── Reusing an EXISTING delegation on the leave form ──────────────────────
//
// ⚠️ Prod 2026-10-22: Damson delegated to Hadija on the Delegations page
// first, Hadija accepted, and the leave form never offered it — it only knew
// a delegation created FROM the form, so Submit stayed disabled and a
// duplicate was made. The form now lists the requester's OWN delegations
// (granted BY them — never ones they received). leave-service applies the
// same rule server-side (delegation_link.existing_delegation_problem); this
// copy only explains it up front.

/** Why a delegation of mine can't cover this leave. */
export type ExistingIneligibleReason = 'starts_late' | 'ends_early' | 'linked_elsewhere';

export interface ExistingDelegationOption {
  delegation: HandoverDelegation;
  /** Delegate has confirmed (active / scheduled) vs still pending. */
  accepted: boolean;
  /** Civil (local) first and last day of the delegation's window. */
  firstDay: string;
  lastDay: string;
  reason?: ExistingIneligibleReason;
}

/** Statuses a delegation can still be picked in: accepted, or awaiting the
 *  delegate / an admin (whether a pending one may be SUBMITTED is the
 *  policy's call — coverageSubmitState, the J8 rule). */
const PICKABLE = new Set(['active', 'scheduled', 'pending_acceptance', 'pending_approval']);
const ACCEPTED = new Set(['active', 'scheduled']);

/** An instant → the local civil date (YYYY-MM-DD); a bare date stays itself. */
export function civilDay(iso?: string | null): string | null {
  if (!iso) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? localCivilToday(t) : null;
}

/** Does the delegation's window cover EVERY day of the leave?
 *  Nov 1–10 covers Nov 2–6; it does not cover Nov 2–12. */
export function windowCovers(d: Pick<Delegation, 'startsAt' | 'endsAt'>, startDate: string, endDate?: string | null): boolean {
  const first = civilDay(d.startsAt);
  const last = civilDay(d.endsAt);
  const end = endDate || startDate;
  return !!first && !!last && first <= startDate && last >= end;
}

/** Is it already covering ANOTHER time-off request that still needs it? */
function linkedElsewhere(d: HandoverDelegation, ownRequestId?: string | null): boolean {
  if (!d.leaveRequestId || d.leaveRequestId === ownRequestId) return false;
  const s = linkedLeaveStatus(d);
  return s === null || s === 'pending' || s === 'approved' || s === 'submitted';
}

/** Sort and split the requester's own delegations for a leave of
 *  [startDate, endDate]: `eligible` (accepted first, newest first) and
 *  `ineligible` (pickable but not for THESE dates / already in use — shown
 *  with the reason so nobody wonders where theirs went). Ended / declined /
 *  revoked ones are left out: they can never cover anything. */
export function existingDelegationOptions(
  asDelegator: HandoverDelegation[] | null | undefined,
  startDate: string,
  endDate?: string | null,
  ownRequestId?: string | null,
): { eligible: ExistingDelegationOption[]; ineligible: ExistingDelegationOption[] } {
  const eligible: ExistingDelegationOption[] = [];
  const ineligible: ExistingDelegationOption[] = [];
  if (!startDate) return { eligible, ineligible };
  const end = endDate || startDate;
  for (const d of asDelegator ?? []) {
    const s = String(d.status ?? '');
    if (d.revokedAt || !PICKABLE.has(s)) continue;
    const firstDay = civilDay(d.startsAt);
    const lastDay = civilDay(d.endsAt);
    if (!firstDay || !lastDay) continue;
    const opt: ExistingDelegationOption = { delegation: d, accepted: ACCEPTED.has(s), firstDay, lastDay };
    if (firstDay > startDate) ineligible.push({ ...opt, reason: 'starts_late' });
    else if (lastDay < end) ineligible.push({ ...opt, reason: 'ends_early' });
    else if (linkedElsewhere(d, ownRequestId)) ineligible.push({ ...opt, reason: 'linked_elsewhere' });
    else eligible.push(opt);
  }
  const newest = (a: ExistingDelegationOption, b: ExistingDelegationOption) => String(b.delegation.createdAt ?? '').localeCompare(String(a.delegation.createdAt ?? ''));
  eligible.sort((a, b) => Number(b.accepted) - Number(a.accepted) || newest(a, b));
  ineligible.sort(newest);
  return { eligible, ineligible };
}

/** The one to preselect so nobody is pushed into a duplicate: an accepted
 *  one first, else the newest pending one. */
export function preferredExisting(eligible: ExistingDelegationOption[]): ExistingDelegationOption | null {
  return eligible[0] ?? null;
}
